// Reconnect policy for the admin circuit (booted by reconnect-boot.ts -> js/reconnect.js, loaded by
// ReconnectModal.razor). Three failures look the same to the user but are not:
//   * network blip / app switch: the circuit is alive in the server's disconnected pool
//     (DisconnectedCircuitRetentionPeriod). Reconnecting restores it with all its state, so say
//     nothing for GRACE_MS and let it happen.
//   * deploy / restart: the circuit died with the process. Blazor reports "rejected"; waiting
//     never helps, reload at once.
//   * signed out (logout elsewhere, AdminSessionValidator ended the session): /_blazor/negotiate is
//     challenged to /login, which Blazor treats as an ordinary failed attempt and keeps retrying for
//     about six minutes. So while an outage lasts we ask GET /api/session ourselves and reload into
//     the sign-in page as soon as it says 401/403.
//
// blazor.web.js 10 dispatches "components-reconnect-state-changed" on the modal: "show" once, then
// "retrying" for EVERY attempt (the first included) and on every 1 s countdown tick, then one of
// "hide", "failed", "rejected", "paused", "resume-failed". Visuals ride on data-state, never on
// classList: Blazor owns the components-reconnect-* classes of this element.

const GRACE_MS = 10_000;
/** Most drops reattach in well under a second; the first check waits so they cost no request. */
const PROBE_FIRST_MS = 2_000;
/** The network may come back only after the first check, so keep asking while the outage lasts. */
const PROBE_EVERY_MS = 10_000;

const COPY = {
    retrying: ["Reconnecting…", "The connection dropped. We are trying to get it back."],
    failed: ["Can't reach the server", "Check your internet connection, then try again."],
} as const;

type Kind = keyof typeof COPY;

/** The two calls of window.Blazor this policy uses. */
export interface BlazorReconnectApi {
    reconnect(): Promise<boolean>;
    resumeCircuit?(): Promise<boolean>;
}

/** Everything the policy touches outside the dialog, injected so the tests can drive it. */
export interface ReconnectEnv {
    doc: Pick<Document, "visibilityState" | "addEventListener">;
    blazor: () => BlazorReconnectApi | undefined;
    reload: () => void;
    fetch: (url: string, init: RequestInit) => Promise<Pick<Response, "status" | "type">>;
    /** GET answers 204 while signed in, 401 signed out, 403 no longer an admin. */
    probeUrl: string;
}

export function initReconnect(modal: HTMLDialogElement, env: ReconnectEnv): void {
    const title = modal.querySelector(".reconnect__title")!;
    const hint = modal.querySelector(".reconnect__hint")!;
    const retryButton = modal.querySelector(".reconnect__retry")!;

    let signalled = "hide"; // the last state Blazor reported
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let probeTimer: ReturnType<typeof setTimeout> | null = null;
    let probing = false;
    let reloading = false;

    const hidden = () => env.doc.visibilityState === "hidden";
    /** Blazor is still retrying on its own. */
    const retrying = () => signalled === "show" || signalled === "retrying";
    /** Blazor has stopped retrying; only Retry (or a reload) can bring the page back. */
    const gaveUp = () => signalled === "failed" || signalled === "paused" || signalled === "resume-failed";

    function clearGrace() {
        if (graceTimer !== null) {
            clearTimeout(graceTimer);
            graceTimer = null;
        }
    }

    function stopProbe() {
        if (probeTimer !== null) {
            clearTimeout(probeTimer);
            probeTimer = null;
        }
    }

    function show(kind: Kind) {
        title.textContent = COPY[kind][0];
        hint.textContent = COPY[kind][1];
        modal.dataset.state = kind;
        if (!modal.open) {
            modal.showModal();
        }
    }

    function conceal() {
        modal.dataset.state = "hidden";
        if (modal.open) {
            modal.close();
        }
    }

    function reloadOnce() {
        if (reloading) {
            return;
        }
        reloading = true;
        clearGrace();
        stopProbe();
        env.reload();
    }

    /** The page is live again, whether or not Blazor said so ("hide" is not sent by every path). */
    function back() {
        signalled = "hide";
        clearGrace();
        stopProbe();
        conceal();
    }

    // Never count time while the tab is hidden: an app switch must be invisible, and a long one
    // must not surface the card the instant the user comes back.
    function startGrace() {
        if (graceTimer !== null || hidden() || modal.open) {
            return;
        }
        graceTimer = setTimeout(() => {
            graceTimer = null;
            if (retrying()) {
                show("retrying");
            }
        }, GRACE_MS);
    }

    function scheduleProbe(delay: number) {
        if (probeTimer !== null || probing || hidden() || reloading) {
            return;
        }
        probeTimer = setTimeout(() => {
            probeTimer = null;
            void checkSession();
        }, delay);
    }

    function probeNow() {
        stopProbe();
        void checkSession();
    }

    async function checkSession() {
        if (probing || hidden() || reloading || !(retrying() || gaveUp())) {
            return;
        }
        probing = true;
        try {
            const res = await env.fetch(env.probeUrl, {
                method: "GET",
                cache: "no-store",
                credentials: "same-origin",
                redirect: "manual",
            });
            if (res.type === "opaqueredirect" || res.status === 401 || res.status === 403) {
                reloadOnce(); // the reload lands on the sign-in page
                return;
            }
            // 204: still signed in, the circuit is just not back yet. 5xx: the server is restarting.
        } catch {
            // Offline: say nothing new and ask again later.
        } finally {
            probing = false;
        }
        if (retrying() || gaveUp()) {
            scheduleProbe(PROBE_EVERY_MS);
        }
    }

    function apply() {
        if (retrying()) {
            startGrace();
            scheduleProbe(PROBE_FIRST_MS);
            return;
        }
        if (gaveUp()) {
            clearGrace();
            show("failed");
            probeNow();
            return;
        }
        if (signalled === "rejected") {
            reloadOnce();
            return;
        }
        back(); // "hide"
    }

    async function retry() {
        clearGrace();
        show("retrying");
        try {
            const blazor = env.blazor()!;
            // reconnect() answers true WITHOUT dispatching "hide" when its connection is already up,
            // so close the card ourselves on every success.
            if (await blazor.reconnect()) {
                back();
                return;
            }
            // Server answered but the circuit is gone (or was paused): try resuming, else reload.
            if (typeof blazor.resumeCircuit === "function" && await blazor.resumeCircuit()) {
                back();
                return;
            }
            reloadOnce();
        } catch {
            show("failed");
            probeNow(); // a negotiate challenged to /login throws here too
        }
    }

    modal.addEventListener("components-reconnect-state-changed", (event) => {
        signalled = (event as CustomEvent<{ state: string }>).detail.state;
        apply();
    });

    // Escape must not dismiss the card: it is the only feedback that the page is not live.
    modal.addEventListener("cancel", (event) => event.preventDefault());

    env.doc.addEventListener("visibilitychange", () => {
        if (hidden()) {
            clearGrace();
            stopProbe();
            return;
        }
        if (gaveUp()) {
            void retry(); // the user is back: try once without making them find the button
            return;
        }
        apply();
    });

    retryButton.addEventListener("click", () => void retry());
}
